import { useState, useEffect } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Bug, LayoutDashboard, Plus, FileText, Download, ChevronRight, AlertTriangle, Globe, LogIn, LogOut, Menu, X, User, Moon, Sun, Heart, MessageSquare, Shield, Scroll, Trophy } from "lucide-react";
import { LanguageProvider, useLanguage } from "@/i18n/LanguageContext";
import { AuthProvider, useAuth, AuthCallback, DiscordCallback } from "@/context/AuthContext";
import { ThemeProvider, useTheme } from "@/context/ThemeContext";
import { ServerStatus } from "@/components/ServerStatus";
import { languageNames } from "@/i18n/translations";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import Dashboard from "@/pages/Dashboard";
import BugList from "@/pages/BugList";
import BugDetail from "@/pages/BugDetail";
import SubmitBug from "@/pages/SubmitBug";
import PrioritySummary from "@/pages/PrioritySummary";
import ExportPage from "@/pages/ExportPage";
import SupportSection from "@/pages/SupportSection";
import FeedbackSection from "@/pages/FeedbackSection";
import AdminPanel from "@/pages/AdminPanel";
import PatchNotes from "@/pages/PatchNotes";
import Leaderboard from "@/pages/Leaderboard";
import Login from "@/pages/Login";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auto-retry once on 429 (rate limit) after a short delay
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (error.response?.status === 429 && config && !config._retried429) {
      config._retried429 = true;
      await new Promise((resolve) => setTimeout(resolve, 2500));
      return axios(config);
    }
    return Promise.reject(error);
  }
);

const NavItem = ({ to, icon: Icon, label, isActive, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
    className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
      isActive
        ? "bg-primary/20 text-primary border-l-2 border-primary"
        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
    }`}
  >
    <Icon className="w-5 h-5" />
    <span className="font-heading font-semibold tracking-wider uppercase text-sm">{label}</span>
    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
  </Link>
);

const LanguageSelector = () => {
  const { language, setLanguage, t } = useLanguage();

  return (
    <div className="p-4 border-t border-border/40">
      <div className="flex items-center gap-2 mb-2">
        <Globe className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">{t('common.language')}</span>
      </div>
      <Select value={language} onValueChange={setLanguage}>
        <SelectTrigger className="bg-black/20 border-border/50 font-body text-sm" data-testid="language-selector">
          <SelectValue>
            <span className="flex items-center gap-2">
              <span>{languageNames[language]?.flag}</span>
              <span>{languageNames[language]?.native}</span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {Object.entries(languageNames).map(([code, { native, flag }]) => (
            <SelectItem key={code} value={code}>
              <span className="flex items-center gap-2">
                <span>{flag}</span>
                <span>{native}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

const ThemeToggle = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="p-4 border-t border-border/40">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {theme === "dark" ? (
            <Moon className="w-4 h-4 text-muted-foreground" />
          ) : (
            <Sun className="w-4 h-4 text-muted-foreground" />
          )}
          <span className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Theme</span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={toggleTheme}
          className="h-8 px-3 font-mono text-xs"
          data-testid="theme-toggle"
        >
          {theme === "dark" ? "Light" : "Dark"}
        </Button>
      </div>
    </div>
  );
};

const UserSection = () => {
  const { user, isAuthenticated, login, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showLoginOptions, setShowLoginOptions] = useState(false);

  if (isAuthenticated && user) {
    return (
      <div className="p-4 border-t border-border/40">
        <div className="flex items-center gap-3 mb-3">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <User className="w-4 h-4 text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-body truncate">{user.name}</p>
            <p className="text-xs text-muted-foreground font-mono truncate">{user.email}</p>
            <p className="text-[10px] text-primary font-mono capitalize">{user.role || 'reporter'}</p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={logout}
          className="w-full font-mono text-xs"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {t('auth.logout')}
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 border-t border-border/40">
      {showLoginOptions ? (
        <div className="space-y-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => login('google')}
            className="w-full font-mono text-xs justify-start"
            data-testid="login-google-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Google
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => login('discord')}
            className="w-full font-mono text-xs justify-start bg-[#5865F2]/10 border-[#5865F2]/30 hover:bg-[#5865F2]/20"
            data-testid="login-discord-btn"
          >
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="#5865F2">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Discord
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowLoginOptions(false)}
            className="w-full font-mono text-xs text-muted-foreground"
          >
            Cancel
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowLoginOptions(true)}
          className="w-full font-mono text-xs"
          data-testid="login-btn"
        >
          <LogIn className="w-4 h-4 mr-2" />
          {t('auth.login')}
        </Button>
      )}
    </div>
  );
};

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { t } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  
  const navItems = [
    { to: "/", icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: "/bugs", icon: Bug, label: t('nav.bugReports') },
    { to: "/submit", icon: Plus, label: t('nav.submitBug') },
    { to: "/priority", icon: AlertTriangle, label: t('nav.priority') },
    { to: "/leaderboard", icon: Trophy, label: "Leaderboard" },
    { to: "/patch-notes", icon: Scroll, label: "Patch Notes" },
  ];

  // Export only visible to admins and developers
  const userRole = user?.role || "reporter";
  if (isAuthenticated && (userRole === "admin" || userRole === "developer")) {
    navItems.push({ to: "/export", icon: Download, label: t('nav.export') });
  }

  const bottomNavItems = [
    { to: "/support", icon: Heart, label: "Support" },
    { to: "/feedback", icon: MessageSquare, label: "Feedback" },
  ];

  // Show admin link only for admins
  const showAdmin = isAuthenticated && userRole === "admin";

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-card border-r border-border/40 
        flex flex-col min-h-screen
        transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="p-4 lg:p-6 border-b border-border/40 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-primary/20 flex items-center justify-center glow">
              <Bug className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg lg:text-xl tracking-widest uppercase text-foreground">BugNexus</h1>
              <p className="text-xs text-muted-foreground font-mono">QA TRIAGE SYSTEM</p>
            </div>
          </div>
          <button
            className="lg:hidden p-2 text-muted-foreground hover:text-foreground"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavItem
              key={item.to}
              {...item}
              isActive={location.pathname === item.to || (item.to !== "/" && location.pathname.startsWith(item.to))}
              onClick={handleNavClick}
            />
          ))}
          
          {/* Bottom Navigation - Support & Feedback */}
          <div className="border-t border-border/40 mt-4 pt-4">
            {bottomNavItems.map((item) => (
              <NavItem
                key={item.to}
                {...item}
                isActive={location.pathname === item.to}
                onClick={handleNavClick}
              />
            ))}
            
            {/* Admin Panel - Only for admins */}
            {showAdmin && (
              <NavItem
                to="/admin"
                icon={Shield}
                label="Admin Panel"
                isActive={location.pathname === "/admin"}
                onClick={handleNavClick}
              />
            )}
          </div>
        </nav>

        {/* Server Status */}
        <ServerStatus />

        {/* User Section */}
        <UserSection />

        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Language Selector */}
        <LanguageSelector />

        {/* Discord Info */}
        <div className="p-4 border-t border-border/40 hidden lg:block">
          <div className="bg-muted/30 rounded-sm p-4">
            <p className="text-xs text-muted-foreground font-mono mb-2">DISCORD WEBHOOK</p>
            <p className="text-[10px] text-muted-foreground/60 break-all font-mono">
              POST /api/discord/webhook
            </p>
          </div>
        </div>
      </aside>
    </>
  );
};

const MobileHeader = ({ onMenuClick }) => {
  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-card border-b border-border/40 px-4 py-3 flex items-center gap-3">
      <button
        onClick={onMenuClick}
        className="p-2 text-muted-foreground hover:text-foreground"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-6 h-6" />
      </button>
      <div className="flex items-center gap-2">
        <Bug className="w-5 h-5 text-primary" />
        <span className="font-heading font-bold text-lg tracking-wider uppercase">BugNexus</span>
      </div>
    </header>
  );
};

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id - must be synchronous during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Check for Discord OAuth callback
  if (location.pathname === '/auth/discord/callback') {
    return <DiscordCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/bugs" element={<BugList />} />
      <Route path="/bugs/:id" element={<BugDetail />} />
      <Route path="/submit" element={<SubmitBug />} />
      <Route path="/priority" element={<PrioritySummary />} />
      <Route path="/patch-notes" element={<PatchNotes />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/export" element={<ExportPage />} />
      <Route path="/support" element={<SupportSection />} />
      <Route path="/feedback" element={<FeedbackSection />} />
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/login" element={<Login />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/auth/discord/callback" element={<DiscordCallback />} />
    </Routes>
  );
}

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex min-h-screen bg-background grid-bg">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />
      <main className="flex-1 overflow-auto pt-14 lg:pt-0">
        <AppRouter />
      </main>
      <Toaster position="bottom-right" theme="dark" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppContent />
            </AuthProvider>
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
